/**
 * Мелкое форматирование, общее для шапки, списка бэклога и личного кабинета.
 *
 * Вынесено из BacklogAside, когда те же две функции понадобились окну профиля: третья копия
 * функции времени разошлась бы с первыми двумя на первой же правке формата, и обнаружилось
 * бы это тем, что одно и то же событие в двух местах экрана подписано по-разному. Ровно это
 * и случилось: к 07.09.2026 копий было ТРИ — здесь, в `TopicPanel` и в `AlertsList`, — и
 * правка зоны показа 08.09 свелась к тому, чтобы свести их обратно в одну.
 *
 * ЗДЕСЬ ЖИВЁТ ЕДИНСТВЕННЫЙ ДОМ ФОРМАТИРОВАНИЯ ВРЕМЕНИ В UI. Заводить четвёртую копию нельзя;
 * если нужна новая форма — она добавляется сюда.
 */

import { ISO_UTC_MS, isoToMs, msToWallMinute } from '@smm/contracts';

/**
 * Форма ISO_UTC_MS фиксирована схемой ('YYYY-MM-DDTHH:MM:SS.mmmZ'). Проверка стоит ДО любого
 * разбора: значение неожиданной формы возвращается как есть, а не превращается в «Invalid
 * Date» посреди экрана согласования.
 */
const ISO_UTC_MS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * ЗОНА ПОКАЗА ИНТЕРФЕЙСА — одна на весь UI (решение Ильи 07.09.2026: «всё время, которое
 * находится в UI, перевести на московское»).
 *
 * До этого интерфейс печатал UTC, и обоснование было честным: часовой пояс приезжает только
 * в `GET /api/v1/brands`, то есть на момент отрисовки шапки его может не быть. Обоснование
 * осталось верным — изменился ВЫБОР: показывать один и тот же момент в двух разных зонах на
 * одном экране (слоты бэкенд уже считает в зоне бренда) хуже, чем зашить зону показа.
 *
 * ЭТО НЕ ЗОНА ПУБЛИКАЦИИ. Зона, в которой материализуется слот и живут дедлайны, по-прежнему
 * берётся из конфига бренда (`config/brands/geoboost.yaml`, `timezone`) и никогда отсюда.
 * У geoboost они совпадают, и это совпадение, а не тождество: второй бренд в другой зоне
 * меняет ОДНУ строку здесь, а не двадцать по местам вызова.
 */
export const DISPLAY_TZ = 'Europe/Moscow';

/**
 * Единица зоны стоит рядом с числом всегда — момент без подписи зоны врёт на три часа тому,
 * кто помнит, что раньше здесь был UTC. Слово одно и объявлено ОДИН раз: подпись, дописанная
 * по местам вызова, разъедется так же, как разъехались три копии нарезки.
 */
const TZ_MARK = 'МСК';

/**
 * Инстант → «стенные» год-месяц-день-час-минута зоны показа, форма 'YYYY-MM-DDTHH:MM'.
 *
 * `msToWallMinute` из `@smm/contracts` — единственный законный путь в зону из UI: `new Date`
 * здесь ловит страж границ (правило R11, `tools/check-boundaries.ts`), а `@smm/contracts` —
 * единственный разрешённый UI пакет из `@smm/*` (R4, `docs/frontend.md`).
 */
function wall(iso: string): string | null {
  return ISO_UTC_MS_RE.test(iso) ? msToWallMinute(isoToMs(iso), DISPLAY_TZ) : null;
}

/** «24.08 12:41 МСК» — без года: список бэклога и шапка живут в пределах текущих недель. */
export function shortMsk(iso: string): string {
  const w = wall(iso);
  if (w === null) return iso;
  return `${w.slice(8, 10)}.${w.slice(5, 7)} ${w.slice(11, 16)} ${TZ_MARK}`;
}

/**
 * «24.08.2026 12:41 МСК» — с годом. Год обязателен там, где величина отвечает на вопрос
 * «свежая это новость или прошлогодняя статья» (панель темы, `docs/PHASE-2.md` §2.1).
 */
export function stampMsk(iso: string): string {
  const w = wall(iso);
  if (w === null) return iso;
  return `${w.slice(8, 10)}.${w.slice(5, 7)}.${w.slice(0, 4)} ${w.slice(11, 16)} ${TZ_MARK}`;
}

/**
 * «12:04 МСК» — если момент в тот же московский день, что `now`, иначе «18.09 12:04 МСК».
 * Для компактных строк, где сегодняшнее — норма, а вчерашнее — исключение: мета-строка
 * «в тесте 12:04 МСК · открыть» полосы решения (docs/release-2026-09-19.md §9.1). Дата не
 * выбрасывается молча: «12:04» у вчерашней отправки прочиталось бы как сегодняшняя.
 */
export function momentMsk(iso: string, now: number): string {
  const w = wall(iso);
  if (w === null) return iso;
  const today = msToWallMinute(now, DISPLAY_TZ).slice(0, 10);
  const time = `${w.slice(11, 16)} ${TZ_MARK}`;
  return w.slice(0, 10) === today ? time : `${w.slice(8, 10)}.${w.slice(5, 7)} ${time}`;
}

/**
 * «19.09, 09:12» — момент в зоне БРЕНДА (зона приходит `GET /api/v1/brands`, как в
 * `DecisionPanel`), а не в зоне показа: таблица прогона мониторинга сверяет время обхода с
 * расписанием бренда. Зона ещё не пришла или непригодна — тот же момент в UTC с явной
 * пометкой: подписать чужое время местным значит соврать на три часа. Значение неожиданной
 * формы возвращается как есть, а не превращается в «Invalid Date» посреди таблицы.
 *
 * Переехала сюда из `monitoringRuns.ts` (19.09.2026): её там поставили временно, до первой
 * правки этого файла, — единственного дома форматирования времени.
 */
export function wallShort(iso: string, timezone: string | null): string {
  if (!ISO_UTC_MS.test(iso)) return iso;
  const ms = isoToMs(iso);
  let w: string;
  let mark = '';
  try {
    w = msToWallMinute(ms, timezone ?? 'UTC');
    if (timezone === null) mark = ' UTC';
  } catch {
    w = msToWallMinute(ms, 'UTC');
    mark = ' UTC';
  }
  return `${w.slice(8, 10)}.${w.slice(5, 7)}, ${w.slice(11, 16)}${mark}`;
}

/**
 * Размер файла так, как его пишет клиент Telegram у документа: «812 Б», «532,1 КБ», «1,2 МБ».
 * Единицы двоичные (1 КБ = 1024 Б) — ими же считает и клиент, и потолок `HUMAN_DOCUMENT_MAX_BYTES`
 * (20 МиБ показываются как «20 МБ», а не как «21 МБ»). Своя функция, а не `Intl`: строка
 * обязана быть одинаковой в браузере и в тесте.
 */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} Б`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${oneDecimal(kb)} КБ`;
  return `${oneDecimal(kb / 1024)} МБ`;
}

/** Один знак после запятой, запятая — русская; «20,0» пишется как «20». */
function oneDecimal(n: number): string {
  const s = (Math.round(n * 10) / 10).toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s.replace('.', ',');
}

/**
 * Инициалы для кружка аватара: первые буквы первых двух слов. Разбор по кодпоинтам
 * (`[...p][0]`), а не по `p[0]`: суррогатная пара разрезалась бы пополам.
 */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => [...p][0] ?? '')
    .join('')
    .toUpperCase();
}

/** Русское склонение по числу: 1 черновик, 2 черновика, 5 черновиков. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
